/*
 * Background Image
 *
 * Supports:
 * - Loading thumbnail
 * - Lazy loading of full resolution src
 * - Webp src for suitable browsers
 */

import React from 'react';
import PropTypes from 'prop-types';
import QueryString from 'query-string';

import Image, { supportsWebp } from './image.jsx';

const MAX_WIDTH = 1440;

/**
 * Class representing a background image
 * @extends React.Component
 * @prop {object} containerClassName // Container classes
 * @prop {object} containerStyle     // Container styles
 * @prop {object} style              // Styles
 * @prop {string} className          // Classes
 * @prop {string} loading            // Thumbnail for loading
 * @prop {string} loadStyle          // Styles for loading
 * @prop {string} src                // Primary source
 * @prop {string} psrc               // Webp source
 */
class BackgroundImage extends Image {
    // Load full resolution
    async load() {
        const webpsupport = await supportsWebp();

        const src = `${this.props.src}?${QueryString.stringify({
            w: this.props.loadWidth ? Math.min(this.props.loadWidth, MAX_WIDTH) : MAX_WIDTH,
            fm: webpsupport ? 'webp' : undefined,
            q: 50
        })}`;

        this.setState(
            {
                loading: true,
                src: src
            },
            () => {
                if (this.state.src) {
                    this.imageNode = new window.Image();
                    this.imageNode.src = this.state.src;

                    if (this.imageNode.complete) {
                        this.handleOnLoad.call(this);
                    } else {
                        this.imageNode.onload = this.handleOnLoad.bind(this);
                    }
                }
            }
        );
    }

    // Remove the loaded listener if the component will unmount
    componentWillUnmount() {
        super.componentWillUnmount();

        if (this.imageNode && this.state.loaded === false) {
            this.imageNode.src = this.imageNode.currentSrc || this.props.src;
            this.imageNode.onload = undefined;
        }
    }

    render() {
        const opacity = 0;
        const src = this.state.src || '';
        const styles = this.props.style || {};

        const defaultStyles = {
            opacity: opacity,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center'
        };

        if (src) {
            defaultStyles.backgroundImage = `url(${src})`;
        }

        return (
            <div
                className={this.props.containerClassName}
                ref={this.containerRef}
                style={{
                    ...{overflow: 'hidden'},
                    ...this.props.containerStyle
                }}>
                {this.props.loading && !this.state.loaded && (
                    <div
                        className={this.props.loadStyle || 'pixelated'}
                        style={{
                            ...defaultStyles,
                            ...styles,
                            ...{
                                ...(this.props.loading && {backgroundImage: `url(${this.props.loading})`}), // only add property if true
                                opacity: '0.5',
                                height: '100%'
                            }
                        }} />
                )}
                {(this.state.loading || this.state.loaded) && <div
                    className={`${this.props.className ? this.props.className : 'absolute h-full w-full'} transition-opacity`}
                    ref={this.imageRef}
                    style={{ ...defaultStyles, ...styles }} />}
            </div>
        );
    }

    static propTypes = {
        className: PropTypes.string,
        containerClassName: PropTypes.string,
        containerStyle: PropTypes.object,
        loading: PropTypes.string,
        loadStyle: PropTypes.string,
        loadWidth: PropTypes.number,
        src: PropTypes.string,
        style: PropTypes.object
    };
}

export default BackgroundImage;
